import { AI_AGENT_CONTEXTS, useAppsStore } from '@/features/apps/AppsStore';
import { useFilteredFlights } from '@/features/flights/FlightsStore';
import { useInventoryDrones, useOiListLive, useReservedFlights } from '@/lib/api/queries';
import { canViewContext } from '@/lib/auth/access';
import { config } from '@/lib/config';
import { useAlertFeedStore } from '@/lib/store/alertFeed';
import { useCurrentRole } from '@/lib/store/auth';
import { useDataStreamStore } from '@/lib/store/dataStream';
import { useDronesStore } from '@/lib/store/drones';
import { useInfraStore } from '@/lib/store/infra';
import { useRtmStore } from '@/lib/store/rtm';
import { useSurvStore } from '@/lib/store/surv';
import { type Context, useUiStore } from '@/lib/store/ui';
import { cn } from '@/lib/utils';
import { Lock } from 'lucide-react';
/**
 * Bottom navigation — mockup `.bottomnav` (L4523–L4615).
 *
 * Mockup parity:
 *   - 64px tall, white bg, top shadow
 *   - 6 equal-width items separated by hairlines
 *   - Each item: 26×26 rounded-square icon-wrap → svg → label → status row
 *     (dot + count) and an optional red warn badge for unacked alerts
 *   - Active item: green top-border, green text, soft green-bg, green-tinted
 *     icon-wrap, blinking dot
 *
 * Icons are the mockup's exact SVG paths (not lucide-react substitutes).
 */
import { type ReactNode, useEffect, useRef } from 'react';
import { useShallow } from 'zustand/shallow';

type Tone = 'ok' | 'warn' | 'crit';

interface NavItem {
  ctx: Context;
  label: string;
  title: string;
  Svg: () => ReactNode;
}

const SVG_DRONE = () => (
  <svg className="ni-svg" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="2" fill="currentColor" />
    <path
      d="M6 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm0 12a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm12-12a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm0 12a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"
      fill="currentColor"
      opacity=".8"
    />
    <path
      d="M6 8v3.5m0 1V16m12-8v3.5m0 1V16M8 6h3.5m1 0H16M8 18h3.5m1 0H16"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
);
const SVG_OI = () => (
  <svg className="ni-svg" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 3 3 9v6l9 6 9-6V9L12 3Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
      fill="none"
    />
    <circle cx="12" cy="12" r="2.2" fill="currentColor" />
  </svg>
);
const SVG_SURV = () => (
  <svg className="ni-svg" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="2.5" fill="currentColor" />
    <path
      d="M8.5 8.5a4.95 4.95 0 0 0 0 7M15.5 8.5a4.95 4.95 0 0 1 0 7"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      fill="none"
    />
    <path
      d="M5.5 5.5a9 9 0 0 0 0 13M18.5 5.5a9 9 0 0 1 0 13"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      fill="none"
      opacity=".45"
    />
  </svg>
);
const SVG_INFRA = () => (
  <svg className="ni-svg" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2v20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" />
    <path
      d="M7 6h10M7 18h10"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      fill="none"
      opacity=".5"
    />
    <path
      d="M5 9l7-3 7 3M5 15l7 3 7-3"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);
const SVG_RTM = () => (
  <svg className="ni-svg" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <rect
      x="3"
      y="4"
      width="18"
      height="5"
      rx="1.5"
      stroke="currentColor"
      strokeWidth="1.6"
      fill="none"
    />
    <rect
      x="3"
      y="11"
      width="18"
      height="5"
      rx="1.5"
      stroke="currentColor"
      strokeWidth="1.6"
      fill="none"
    />
    <circle cx="7" cy="6.5" r="1" fill="currentColor" />
    <circle cx="10.5" cy="6.5" r="1" fill="currentColor" opacity=".4" />
    <circle cx="7" cy="13.5" r="1" fill="currentColor" />
    <circle cx="10.5" cy="13.5" r="1" fill="currentColor" opacity=".4" />
  </svg>
);
const SVG_INV = () => (
  <svg className="ni-svg" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <rect
      x="3"
      y="3"
      width="7"
      height="7"
      rx="1.2"
      stroke="currentColor"
      strokeWidth="1.6"
      fill="none"
    />
    <rect
      x="14"
      y="3"
      width="7"
      height="7"
      rx="1.2"
      stroke="currentColor"
      strokeWidth="1.6"
      fill="none"
    />
    <rect
      x="3"
      y="14"
      width="7"
      height="7"
      rx="1.2"
      stroke="currentColor"
      strokeWidth="1.6"
      fill="none"
    />
    <rect
      x="14"
      y="14"
      width="7"
      height="7"
      rx="1.2"
      stroke="currentColor"
      strokeWidth="1.6"
      fill="none"
    />
  </svg>
);
const SVG_DS = () => (
  <svg className="ni-svg" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4 7h16M4 12h16M4 17h16"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      fill="none"
    />
    <circle cx="7" cy="7" r="1.4" fill="currentColor" />
    <circle cx="13" cy="12" r="1.4" fill="currentColor" />
    <circle cx="9" cy="17" r="1.4" fill="currentColor" />
  </svg>
);
const SVG_APPS = () => (
  <svg className="ni-svg" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <rect
      x="4"
      y="4"
      width="7"
      height="7"
      rx="1.2"
      stroke="currentColor"
      strokeWidth="1.6"
      fill="none"
    />
    <rect
      x="13"
      y="4"
      width="7"
      height="7"
      rx="1.2"
      stroke="currentColor"
      strokeWidth="1.6"
      fill="none"
    />
    <rect
      x="4"
      y="13"
      width="7"
      height="7"
      rx="1.2"
      stroke="currentColor"
      strokeWidth="1.6"
      fill="none"
    />
    <path
      d="M15 18h4M17 16v4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
);

const SVG_FLIGHTS = () => (
  <svg className="ni-svg" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <title>Flights</title>
    <path
      d="M2.5 19.5 21 12 2.5 4.5l1.8 6.3L15 12l-10.7 1.2z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

const NAV: NavItem[] = [
  { ctx: 'drones', label: 'Drones', title: 'Active Drones', Svg: SVG_DRONE },
  { ctx: 'oi', label: "OI's", title: 'Operational Intents', Svg: SVG_OI },
  { ctx: 'flights', label: 'Flights', title: 'Flights — Historical & Planned', Svg: SVG_FLIGHTS },
  { ctx: 'surv', label: 'Surveillance', title: 'Surveillance Networks', Svg: SVG_SURV },
  { ctx: 'infra', label: 'Infrastructure', title: 'Ground Infrastructure', Svg: SVG_INFRA },
  { ctx: 'rtm', label: 'RTM Services', title: 'Real-Time Monitoring Services', Svg: SVG_RTM },
  { ctx: 'inv', label: 'Inventory', title: 'Inventory — Users & Drones', Svg: SVG_INV },
  { ctx: 'apps', label: 'Apps', title: 'Apps — Tenant App Configuration', Svg: SVG_APPS },
  { ctx: 'ds', label: 'Data Stream', title: 'MQTT Data Stream', Svg: SVG_DS },
];

export function NavRail() {
  const ctx = useUiStore((s) => s.activeContext);
  const setCtx = useUiStore((s) => s.setContext);
  const role = useCurrentRole();
  const navRef = useRef<HTMLElement>(null);
  // ENG-318 — on a phone the nine tabs outrun the screen and the bar scrolls
  // sideways; keep the selected tab in view (a no-op when everything fits).
  // biome-ignore lint/correctness/useExhaustiveDependencies: `ctx` is the trigger — the active button is found by its data attribute
  useEffect(() => {
    navRef.current
      ?.querySelector<HTMLElement>('[data-active="true"]')
      ?.scrollIntoView?.({ inline: 'center', block: 'nearest' });
  }, [ctx]);
  // Locked contexts show no totals and fetch nothing for them — ENG-281.
  const flightsLocked = !canViewContext('flights', role);
  const invLocked = !canViewContext('inv', role);

  // Live counts (drones from fleet, others from their stores or REST cache)
  const fleet = useDronesStore(useShallow((s) => Object.values(s.byId)));
  const alerts = useAlertFeedStore((s) => s.items);
  const dronesActive = fleet.length;
  const dronesWarn = fleet.filter(
    (d) =>
      d.telemetry.status === 'SA' ||
      d.telemetry.status === 'LOST' ||
      d.telemetry.ops_status === 'Non-Normal',
  ).length;
  const dronesAlerts = alerts.filter(
    (a) =>
      a.drone_id != null &&
      a.acked_at == null &&
      (a.severity === 'critical' || a.severity === 'warning'),
  ).length;

  const { data: oiItems } = useOiListLive();
  const oiActive = (oiItems ?? []).filter((o) => o.status === 'Active').length;

  const survServices = useSurvStore(useShallow((s) => Object.values(s.byId)));
  const survActive = survServices.filter((s) => s.service_state === 'Active').length;
  const survDegraded = survServices.filter((s) => s.service_state === 'Degraded').length;
  const survConnecting = survServices.filter((s) => s.service_state === 'Connecting').length;
  const survOffline = survServices.filter((s) => s.service_state === 'Offline').length;

  const infraSites = useInfraStore(useShallow((s) => Object.values(s.byId)));
  const infraDegraded = infraSites.filter((s) => s.status === 'degraded').length;
  const infraOffline = infraSites.filter((s) => s.status === 'inactive').length;

  const rtmPods = useRtmStore(useShallow((s) => Object.values(s.byName)));
  const rtmDegraded = rtmPods.filter((p) => p.pod_status === 'degraded').length;

  // The badge re-bases per plane. Demo has a working DroneLogBook sync, so
  // "pending" means unsynced — and demo's list is local (MSW), so reading it
  // here is free. Live has no sync endpoint at all, so the number an operator
  // can act on is the upcoming scheduled flights — fetched via the small
  // reserved-only call rather than the full paged history, because NavRail is
  // mounted in EVERY context (see fetchReservedFlights).
  const { data: demoFlights } = useFilteredFlights({ enabled: config.isMock && !flightsLocked });
  const { data: reservedFlights } = useReservedFlights({ enabled: !flightsLocked });
  const flightsPending = (demoFlights ?? []).filter(
    (f) => f.sync_status === 'Pending' || f.sync_status === 'Failed',
  ).length;
  const flightsPlanned = (reservedFlights ?? []).length;
  const flightsBadge = config.isMock ? flightsPending : flightsPlanned;

  const { data: invDrones } = useInventoryDrones({ enabled: !invLocked });
  const invCount = invDrones?.length ?? 0;

  const dsMsgs = useDataStreamStore((s) => s.totalMsgs);
  const aiEnabled = useAppsStore((s) => s.aiEnabled);
  const djiConfigured = useAppsStore((s) => s.dji.configured);
  const appsEnabled =
    AI_AGENT_CONTEXTS.filter((ctx) => aiEnabled[ctx]).length + (djiConfigured ? 1 : 0);

  const counts: Record<Context, { count: string; tone: Tone; blink?: boolean }> = {
    drones: {
      count: dronesActive === 0 ? 'Awaiting' : `${dronesActive} Active`,
      tone: dronesWarn > 0 ? 'warn' : 'ok',
      blink: true,
    },
    oi: {
      count: oiActive > 0 ? `${oiActive} Active` : `${(oiItems ?? []).length} Total`,
      tone: 'ok',
    },
    flights: {
      // "Pending" in both planes, per the mockup. The number behind it
      // re-bases (demo: unsynced flights; live: upcoming reserved ones —
      // see flightsBadge), but both are flights still awaiting something.
      count: `${flightsBadge} Pending`,
      tone: flightsBadge > 0 ? 'warn' : 'ok',
    },
    // Priority: connecting (cold start) > degraded > active > offline.
    // Once cold start is done, the operator should see the positive
    // Active count by default — Offline services are still visible in
    // the SurvList panel. Only fall through to "Offline" when literally
    // nothing is active (broker fully silent).
    surv: {
      count:
        survConnecting > 0
          ? `${survConnecting} Connecting`
          : survDegraded > 0
            ? `${survDegraded} Degraded`
            : survActive > 0
              ? `${survActive} Active`
              : `${survOffline} Offline`,
      tone: survDegraded > 0 ? 'warn' : survActive > 0 ? 'ok' : survConnecting > 0 ? 'ok' : 'crit',
    },
    infra: {
      count:
        infraOffline > 0
          ? `${infraOffline} Offline`
          : infraDegraded > 0
            ? `${infraDegraded} Degraded`
            : `${infraSites.length} Online`,
      tone: infraOffline > 0 ? 'crit' : infraDegraded > 0 ? 'warn' : 'ok',
    },
    rtm: {
      count: rtmDegraded > 0 ? `${rtmDegraded} Degraded` : `${rtmPods.length} Healthy`,
      tone: rtmDegraded > 0 ? 'warn' : 'ok',
    },
    inv: { count: `${invCount} Drones`, tone: 'ok' },
    ds: { count: dsMsgs > 0 ? `${dsMsgs} msgs` : 'Idle', tone: 'ok' },
    apps: {
      count: `${appsEnabled} Enabled`,
      tone: appsEnabled > 0 ? 'ok' : 'crit',
    },
  };

  return (
    <nav
      ref={navRef}
      className="fixed inset-x-0 bottom-0 z-30 flex h-(--nav-h) pb-[env(safe-area-inset-bottom)] overflow-x-auto overscroll-x-contain scrollbar-none bg-(--color-surface) border-t border-(--color-border)"
      style={{ boxShadow: '0 -2px 8px rgba(0,0,0,.06)' }}
    >
      {NAV.map(({ ctx: c, label, title, Svg }, idx) => {
        const active = c === ctx;
        const meta = counts[c];
        const locked = !canViewContext(c, role);
        return (
          <button
            key={c}
            type="button"
            onClick={() => setCtx(c)}
            title={title}
            data-active={active}
            className={cn(
              'flex-1 flex flex-col items-center justify-center gap-0 relative cursor-pointer transition-[background-color,border-color] duration-150',
              'border-t-2 border-transparent pt-2 pb-1.5 px-1',
              idx < NAV.length - 1 && 'border-r border-(--color-border-lite)',
              active
                ? 'border-t-(--color-green) bg-(--color-green-bg) text-(--color-green)'
                : 'text-(--color-text3) hover:bg-(--color-bg)',
            )}
          >
            <span
              className={cn(
                'w-[26px] h-[26px] flex items-center justify-center rounded-[7px] transition-colors',
                active && 'bg-(--color-green-bg)',
              )}
              style={active ? { backgroundColor: 'rgba(29,122,64,.1)' } : undefined}
            >
              <span className="block w-4 h-4 [&_.ni-svg]:w-full [&_.ni-svg]:h-full">
                <Svg />
              </span>
            </span>
            <span
              className={cn(
                'text-[0.731em] font-bold tracking-[0.2px] leading-none mt-1 font-sans',
                active ? 'text-(--color-green)' : 'text-(--color-text3)',
              )}
            >
              {label}
            </span>
            {locked ? (
              <span className="flex items-center mt-[3px] h-3 text-[0.692em] font-bold font-mono leading-none text-(--color-text3)">
                Locked
              </span>
            ) : (
              <span className="flex items-center gap-[3px] mt-[3px] h-3">
                <span
                  className={cn(
                    'w-[5px] h-[5px] rounded-full shrink-0',
                    meta.tone === 'ok' && 'bg-[#22c55e]',
                    meta.tone === 'warn' && 'bg-[#f59e0b]',
                    meta.tone === 'crit' && 'bg-[#ef4444]',
                    meta.blink && active && 'animate-[pulse-dot_2s_ease-in-out_infinite]',
                  )}
                  style={{
                    boxShadow:
                      meta.tone === 'ok'
                        ? '0 0 0 2px rgba(34,197,94,.2)'
                        : meta.tone === 'warn'
                          ? '0 0 0 2px rgba(245,158,11,.2)'
                          : '0 0 0 2px rgba(239,68,68,.2)',
                  }}
                />
                <span
                  className={cn(
                    'text-[0.692em] font-bold font-mono leading-none',
                    active ? 'text-(--color-green)' : 'text-(--color-text3)',
                  )}
                >
                  {meta.count}
                </span>
              </span>
            )}
            {c === 'drones' && dronesAlerts > 0 && (
              <span className="absolute top-1.5 right-[calc(50%-20px)] min-w-[14px] h-[14px] rounded-[7px] px-[3px] bg-(--color-amber) text-(--color-on-accent) text-[0.615em] font-bold leading-none flex items-center justify-center border-2 border-white">
                {dronesAlerts}
              </span>
            )}
            {locked && (
              <span
                className="absolute top-1.5 right-2 text-(--color-text3)"
                title="Sign in to view"
              >
                <Lock size={9} />
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
}
